import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  CLAIMS_EXPORT_PATH,
  CLAIM_NEXT,
  captureAuthorizeBillingAgent,
  financeInvoicePreview,
  captureSummaryV3,
  claimStatusSet,
  clearinghouseConfig,
  clearinghouseSave,
  clearinghouseSubmit,
  feeSchedule,
  financeBilling,
  financeBillingClaims,
  fmtCount,
  fmtMoneyCompact,
  practitionerSetActive,
  reimbursementAddPractitioner,
  reimbursementClaims,
  reimbursementCodes,
  reimbursementGenerate,
  reimbursementPractitioners,
  reimbursementSummary,
  residentBillerAssign,
  type CatalogCode,
  type ClaimStatus,
  type ExportClaim,
  type InvoicePreview,
} from '@aithora/shared';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../../session/SessionContext';
import { useDemoBuilding } from '../../session/DemoScope';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { sx } from '../../ui/designStyles';
import { MedicaidLookup } from './MedicaidLookup';
import { CAPT, badgeOf, cellS, chip, currentPeriod, downloadFrom, figS, k1, notServed, pre, previousPeriod, useFinanceApi, whole } from './lib';
import { useBuildLabels, useBuildText, useCustomerText } from '../../ui/buildLabels';
import { facilitiesParam, usePickedIds } from '../../session/FacilityScope';
import { useConsoleRole } from '../../session/SessionContext';
import { SHARE_DEFAULT, clampPct, pilotNote, usePilot, type ShareConfig } from './pilot';
import { ShareSettings, YourBill } from './PilotBlocks';

const CREDS = ['NP', 'MD', 'DO', 'PA', 'CNS', 'CNM'];
const btn = (primary: boolean) =>
  'display:inline-flex;align-items:center;justify-content:center;height:32px;padding:0 14px;border-radius:8px;font-size:13px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
  (primary ? 'background:#2F5C97;color:#fff;' : 'background:#FCFAF4;color:#3C454F;border:1px solid rgba(34,39,46,.18);');
const seg = (on: boolean) =>
  'display:inline-flex;align-items:center;height:28px;padding:0 12px;border-radius:6px;font-size:13px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
  (on ? 'background:#2F5C97;color:#fff;' : 'background:transparent;color:#54606C;');
const HEAD = 'flex:0 0 auto;display:flex;align-items:baseline;gap:2px 10px;flex-wrap:wrap;padding-bottom:6px;border-bottom:1px solid rgba(34,39,46,.12);min-width:0;';
const TITLE = 'font-size:14px;font-weight:800;color:#22272E;letter-spacing:-.01em;white-space:nowrap;';
const META = 'font-size:14px;color:#54606C;min-width:0;';
const MONO = 'font-variant-numeric:tabular-nums;';
const CHIP_OK = chip('#12503C', 'rgba(47,138,111,.16)');
const CHIP_GREY = chip('#3C454F', 'rgba(34,39,46,.18)');

const aiChip = (c: CatalogCode) => (c.aiCapture === 'assists' ? 'AI CAPTURE' : c.aiCapture === 'requires_team' ? 'CARE TEAM' : 'EXAM NEEDED');
const perWord = (f: CatalogCode['frequency']) => (f === 'monthly' ? 'month' : f === 'quarterly' ? 'quarter' : f === 'annual' ? 'year' : 'event');

/**
 * Finance, Billing (design fnIsBilling, T10853-11028): the bill, the period's claims and
 * who attests them, the code catalog, what would be denied today, and the clearinghouse.
 * Atlas documents it; the facility bills it. Every write goes to the reimbursement
 * routes; nothing reaches a payer from a sandbox.
 */

/** A control that acts only when live; otherwise the same look as a label, with why on hover. */
function Ctl({ live, why, onClick, style, children, ...aria }: { live: boolean; why?: string; onClick: () => void; style: string; children: ReactNode; 'aria-label'?: string; 'aria-pressed'?: boolean }) {
  if (!live) {
    return (
      <span data-readonly="true" aria-label={aria['aria-label']} title={why} style={sx(style + 'cursor:default;')}>
        {children}
      </span>
    );
  }
  return (
    <a href="#" {...aria} onClick={(e) => { pre(e); onClick(); }} style={sx(style)}>
      {children}
    </a>
  );
}

export function Billing({ facility, facName, canAct, desk = false, roTitle, readOnlyFacility = false }: { facility: string | undefined; facName?: string; canAct: boolean; desk?: boolean; roTitle?: string; readOnlyFacility?: boolean }) {
  const plain = useCustomerText();
  // A read-only account sees the write controls drawn as off, not as live solid buttons.
  const off = canAct ? '' : 'opacity:.55;cursor:not-allowed;';
  const { client, session } = useSession();
  const { realRole, viewingAs } = useConsoleRole();
  // The building's pilot (V9: $0 during the free pilot) and the share switch, which only a
  // Super Admin sees and only on this page until the back end stores it.
  const { pilot } = usePilot(facility ?? session?.user.organizationId ?? null);
  const [share, setShare] = useState<ShareConfig>(SHARE_DEFAULT);
  const superAdmin = realRole === 'SUPER_ADMIN' && !viewingAs && !readOnlyFacility;
  // In a seeded building scope (no banner, no "demo" on screen): no sample tag on each figure.
  const demoBuilding = useDemoBuilding();
  const navigate = useNavigate();
  const now = Date.now();
  // Which month: the last full month (the default, what is billed), or the month in
  // progress. A month in progress is never submitted (Andrew, 2026-10-01; the server
  // refuses it too): this month shows what has qualified so far and bills once it closes.
  const [which, setWhich] = useState<'last' | 'this'>('last');
  const period = which === 'this' ? currentPeriod(now) : previousPeriod(now);
  const org = facility;
  const bill = useFinanceApi((c, signal) => financeBilling(c, { query: { facility, period }, signal }), [facility, period]);
  const summary = useFinanceApi((c, signal) => reimbursementSummary(c, { query: { organizationId: org, period }, signal }), [org, period]);
  // The capture line follows the facility switcher's pick, like Capture.
  const facilities = facilitiesParam(usePickedIds());
  const network = useFinanceApi((c, signal) => captureSummaryV3(c, { query: facilities ? { facilities } : undefined, signal }), [facilities]);
  // A Facility Admin reads their own building's claims, status and money expected, never
  // submits, and sees a masked code as withheld (Andrew, 2026-09-30): the building-scoped
  // read. Aithora's billing desk keeps the operator routes.
  type FacMoney = { expectedCents: number; paidCents: number; deniedCents: number };
  const claimsQ = useFinanceApi<{ claims: ExportClaim[]; money: FacMoney | null }>(
    (c, signal) =>
      readOnlyFacility
        ? financeBillingClaims(c, { query: { period }, signal }).then((r) => ({
            claims: r.claims.map((x) => ({ ...x, cpt: x.cpt ?? '' })),
            money: { expectedCents: r.expectedCents, paidCents: r.paidCents, deniedCents: r.deniedCents },
          }))
        : reimbursementClaims(c, { query: { organizationId: org, period }, signal }).then((r) => ({ claims: r.claims, money: null })),
    [org, period, readOnlyFacility],
  );
  const pracQ = useFinanceApi((c, signal) => reimbursementPractitioners(c, { query: { organizationId: org }, signal }), [org], !readOnlyFacility);
  const codesQ = useFinanceApi((c, signal) => reimbursementCodes(c, { query: { organizationId: org }, signal }), [org], !readOnlyFacility);
  const fee = useFinanceApi((c, signal) => feeSchedule(c, { signal }), []);
  const cfg = useFinanceApi((c, signal) => clearinghouseConfig(c, { query: { organizationId: org }, signal }), [org], !readOnlyFacility);
  const bt = useBuildText();
  const buildLabels = useBuildLabels();

  const [copied, setCopied] = useState(false);
  const [genErr, setGenErr] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [form, setForm] = useState({ npi: '', first: '', last: '', cred: 0, ptan: '', enrolled: false });
  const [pracErr, setPracErr] = useState(false);
  const [ch, setCh] = useState<{ provider: 'stedi' | 'claimmd'; environment: 'sandbox' | 'production'; enabled: boolean } | null>(null);
  const [saved, setSaved] = useState(false);
  const [submitted, setSubmitted] = useState<number | null>(null);
  // The 837P billing identity (organizations.billing_npi, tax_id, payer_id, payer_name).
  const [ident, setIdent] = useState<{ billingNpi: string; taxId: string; payerId: string; payerName: string } | null>(null);
  // Billing-agent authorizations recorded on this visit, and the one being recorded.
  const [authFor, setAuthFor] = useState<{ id: string; name: string } | null>(null);
  const [authed, setAuthed] = useState<Record<string, boolean>>({});
  // The monthly invoice, previewed (a dry run: nothing goes to Stripe).
  const [inv, setInv] = useState<InvoicePreview | null>(null);
  const npiRef = useRef<HTMLInputElement>(null);
  // The billing desk's per-claim controls (Aithora only; the routes are operator-gated).
  const [menu, setMenu] = useState<{ key: string; kind: 'state' | 'biller'; top: number; right: number } | null>(null);
  const [deny, setDeny] = useState<ExportClaim | null>(null);
  const [dl, setDl] = useState<string | null>(null);

  useEffect(() => {
    if (cfg.data && !ch) {
      const a = cfg.data.account;
      setCh({ provider: a.provider, environment: a.environment, enabled: a.enabled });
      setSaved(a.enabled);
    }
    if (cfg.data && !ident) {
      const bi = cfg.data.billingIdentity ?? {};
      setIdent({ billingNpi: bi.billingNpi ?? '', taxId: bi.taxId ?? '', payerId: bi.payerId ?? '', payerName: bi.payerName ?? '' });
    }
  }, [cfg.data, ch, ident]);

  // The clearinghouse choices act only where this account can change them and the
  // building's account has loaded; elsewhere they are read as labels, with the reason.
  const chLive = canAct && !!ch;
  const chWhy = !canAct ? roTitle : !ch ? 'The clearinghouse account has not loaded.' : undefined;
  const b = bill.data;
  const s = summary.data;
  const claims = claimsQ.data?.claims ?? [];
  const facMoney = claimsQ.data?.money ?? null;
  const allPrac = pracQ.data?.practitioners ?? [];
  const prac = allPrac.filter((p) => p.active);
  const codes = codesQ.data?.codes ?? [];
  const f = fee.data;
  const sample = (k: keyof NonNullable<typeof b>['tileBasis']) => (b && b.tileBasis[k] === 'sample' && !demoBuilding && buildLabels ? ' · sample' : '');

  // V9 pricing (design blTiles, L27298): $0 during the free pilot with what Aithora added; after
  // it, occupied beds × $99, the rate, and the share of added money (off until legal review).
  // Andrew, 2026-09-30: per occupied bed only; no per-decision or outcome fee tile.
  const inPilot = !!pilot?.inPilot;
  const beds = b ? b.tiles.residents : null;
  const pricingTiles = inPilot
    ? [
        { label: 'This month', fig: '$0', note: pilotNote(pilot!), c: '#22272E' },
        { label: 'Added money this month', fig: '–', note: 'saved, new billing, beds filled · not measured yet', c: '#54606C' },
      ]
    : [
        {
          label: 'This month',
          fig: b ? whole(b.tiles.usageCents) : '–',
          // Occupied beds are the 30-day average daily census, counted even when not billed
          // (billing exempt): "0 occupied beds" beside 40 residents read as an empty building.
          note: b
            ? beds + ' occupied ' + (beds === 1 ? 'bed' : 'beds') + (b.tiles.billed === false ? ' · 30-day average · not billed' : ' × ' + whole(b.tiles.ratePerResidentCents)) + sample('perResident')
            : '–',
          c: '#22272E',
        },
        { label: 'Per occupied bed', fig: b ? whole(b.tiles.ratePerResidentCents) : '–', note: 'per month', c: '#22272E' },
        { label: 'Share of added money', fig: share.on ? clampPct(share.pct) + '%' : 'Off', note: share.on ? 'of added money, once measured' : 'off until legal review', c: '#22272E' },
      ];
  const tiles = [
    ...pricingTiles,
    {
      label: 'Money protected',
      fig: b ? k1(b.tiles.protectedCents / 100) : '–',
      note: b ? (b.tileBasis.protected === 'sample' ? (demoBuilding || !buildLabels ? 'no live source' : 'sample · no live source') : 'measured') : notServed(bill.error) ? plain('not served yet') : '–',
      c: '#3C454F',
    },
  ];

  const code = (k: string) => s?.byCode[k] ?? null;
  const per = (x: { eligible: number; amountCents: number } | null) => (x && x.eligible ? Math.round(x.amountCents / x.eligible / 100) : 0);
  const capCents = s?.capturableCents ?? null;
  const capture = [
    { k: 'CCM · 99490', v: code('99490')?.amountCents ? whole(code('99490')!.amountCents) : '-', note: (code('99490')?.eligible ?? 0) + ' eligible · $' + per(code('99490')) + '/pt', c: code('99490')?.amountCents ? '#1F6B52' : '#54606C' },
    { k: 'BHI · 99484', v: code('99484')?.amountCents ? whole(code('99484')!.amountCents) : '-', note: (code('99484')?.eligible ?? 0) + ' eligible · $' + per(code('99484')) + '/pt', c: code('99484')?.amountCents ? '#1F6B52' : '#54606C' },
    { k: 'RPM · 99457', v: code('99457')?.amountCents ? whole(code('99457')!.amountCents) : '-', note: 'device-gated · ' + (s?.pairedDevices ?? 0) + ' paired', c: code('99457')?.amountCents ? '#1F6B52' : '#54606C' },
    { k: 'Capturable / mo', v: capCents != null ? whole(capCents) : '–', note: 'Medicare · already billable', c: '#2F5C97' },
    // V8 pricing (Oct 28): the "Net vs subscription" tile is gone.
  ];
  // The top line (design blTopLine): what can still be billed this month, from the reimbursement summary.
  const eligibleN = s ? Object.values(s.byCode).reduce((a, x) => a + x.eligible, 0) : 0;
  const topLine =
    capCents == null
      ? null
      : capCents > 0
        ? 'This month you can still bill ' + whole(capCents) + ' across ' + eligibleN + ' eligible ' + (eligibleN === 1 ? 'claim' : 'claims') + '.'
        : 'Nothing more to bill this month yet. Capture shows who qualifies as care is documented.';

  // The Overview's capture line (design ovAgingLine), from the network capture summary.
  const n = network.data;
  const agingLine =
    n && n.engine && n.denialRate
      ? bt('Engine ' + (n.engine.live ? 'live' : 'not live') + ' · ' + fmtCount(n.engine.connectedFacilities) + ' ' + (n.engine.connectedFacilities === 1 ? 'facility' : 'facilities') +
        ' connected · dollars, windows and denials ' + (n.recoverable.basis === 'measured' ? 'MEASURED' : 'MODELED') + ' · ' + n.denialRate.pct + '% denial rate (' + n.denialRate.source + ') · ' +
        fmtMoneyCompact(n.recoverable.value == null ? null : (n.recoverable.value / 100) * (n.denialRate.pct / 100)) + ' denials · root cause and AR 90+ pending the RCM rollup')
      : '';

  const generate = async () => {
    if (!prac.length) {
      setGenErr(true);
      return;
    }
    setBusy('gen');
    setErr(null);
    try {
      await reimbursementGenerate(client, { query: { organizationId: org, period }, body: {} });
      claimsQ.reload();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const pracValid = form.npi.trim().length >= 10 && !!form.last.trim();
  const addPrac = async () => {
    if (!pracValid) {
      setPracErr(true);
      return;
    }
    setBusy('prac');
    setErr(null);
    try {
      await reimbursementAddPractitioner(client, {
        body: { organizationId: org, npi: form.npi.trim(), firstName: form.first.trim(), lastName: form.last.trim(), credential: CREDS[form.cred], ptan: form.ptan.trim() || undefined, medicareEnrolled: form.enrolled },
      });
      setForm({ npi: '', first: '', last: '', cred: form.cred, ptan: '', enrolled: false });
      setPracErr(false);
      setGenErr(false);
      pracQ.reload();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const saveSetup = async () => {
    if (!ch || !org) return;
    setBusy('save');
    setErr(null);
    try {
      const bi = ident
        ? Object.fromEntries(Object.entries(ident).map(([k, v]) => [k, v.trim()]).filter(([, v]) => v !== ''))
        : {};
      await clearinghouseSave(client, { body: { organizationId: org, provider: ch.provider, environment: ch.environment, enabled: ch.enabled, ...(Object.keys(bi).length ? { billingIdentity: bi } : {}) } });
      setSaved(true);
      cfg.reload();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const monthOpen = which === 'this';
  const canSubmit = !monthOpen && claims.length > 0 && !!ch?.enabled && saved;
  const preview = async () => {
    if (!org) return;
    setBusy('inv');
    setErr(null);
    try {
      setInv(await financeInvoicePreview(client, { query: { facility: org, period: currentPeriod(now) } }));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const submit = async () => {
    if (!canSubmit) return;
    setBusy('submit');
    setErr(null);
    try {
      const r = await clearinghouseSubmit(client, { query: { organizationId: org, period }, body: {} });
      setSubmitted(r.submitted);
      claimsQ.reload();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const act = async (key: string, fn: () => Promise<unknown>, reload: () => void) => {
    setBusy(key);
    setErr(null);
    setMenu(null);
    try {
      await fn();
      reload();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const setStatus = (c: ExportClaim, status: ClaimStatus, denialReason?: string) =>
    claimStatusSet(client, { body: { residentId: c.residentId, cptCode: c.cpt, period: c.period, status, denialReason } });
  const moveClaim = (c: ExportClaim, status: ClaimStatus) => void act('claim', () => setStatus(c, status), claimsQ.reload);
  const assignBiller = (c: ExportClaim, practitionerId: string) =>
    void act('claim', () => residentBillerAssign(client, { params: { id: c.residentId }, body: { practitionerId, role: c.cpt === '99484' ? 'bhi' : 'ccm' } }), claimsQ.reload);
  const togglePrac = (id: string, active: boolean) => void act('prac:' + id, () => practitionerSetActive(client, { params: { id }, body: { active } }), pracQ.reload);
  const download = async (format: 'x12' | 'csv') => {
    setDl(format);
    const q = '?format=' + format + '&period=' + encodeURIComponent(period) + (org ? '&organizationId=' + encodeURIComponent(org) : '');
    const ok = await downloadFrom(client, CLAIMS_EXPORT_PATH + q, format === 'x12' ? 'aithora-837p-' + period + '.edi' : 'aithora-claims-' + period + '.csv').catch(() => false);
    setDl(ok ? null : 'failed');
  };
  const keyOf = (c: ExportClaim) => c.residentId + ':' + c.cpt;
  const isOpen = (c: ExportClaim, kind: 'state' | 'biller') => !!menu && menu.key === keyOf(c) && menu.kind === kind;
  const billerText = (c: ExportClaim) => (c.biller ? c.biller.name + ', ' + c.biller.credential : c.biller === null ? 'Not assigned' : '-');
  const keyOk = !!ch && !!cfg.data?.providers.find((p) => p.provider === ch.provider)?.[ch.environment];
  const provName = ch?.provider === 'claimmd' ? 'Claim.MD' : 'Stedi';
  const envName = ch?.environment === 'production' ? 'Production' : 'Sandbox';

  const byStatus = (k: string) => claims.filter((c) => String(c.status).toLowerCase() === k.toLowerCase());
  const pipe = ['Draft', 'Ready', 'Submitted', 'Paid', 'Denied'].map((k) => {
    const list = byStatus(k);
    const cents = list.reduce((a, c) => a + Math.round(c.amount * 100), 0);
    return { k, n: String(list.length), amt: list.length ? whole(cents) : '$0', c: k === 'Denied' && list.length ? '#B3261E' : k === 'Paid' && list.length ? '#1F6B52' : '#22272E' };
  });
  const flow = [
    ['1 · Care delivered', claims.length ? 'logged' : 'Atlas logs it', 'documented same day', '#2F5C97'],
    ['2 · Claim built', claims.length ? 'drafted' : '–', 'target ≤ 48h after care', 'rgba(47,92,151,.45)'],
    ['3 · Paid', claims.length ? 'pending' : '–', 'Medicare ~14d after a clean claim', 'rgba(34,39,46,.18)'],
  ];
  const health: [string, string, string][] = [
    ['Clean claims', 'front-end: eligibility, coding', '≥ 95%'],
    ['Denial rate', 'check by reason below', '< 5%'],
    ['Days to payment', 'Medicare pays ~14d clean', '≤ 30d'],
    ['Charge lag', 'care date to claim', '≤ 48h'],
    ['Net collection', 'of what was collectible', '≥ 95%'],
  ];
  const summaryText = () =>
    [
      'Aithora billing · ' + period,
      ...tiles.map((t) => t.label + ': ' + t.fig + ' (' + t.note + ')'),
      ...capture.map((c) => c.k + ': ' + c.v + ' (' + c.note + ')'),
      'Claims: ' + claims.length,
    ].join('\n');
  const verified = f?.verified ?? 0;
  const total = f?.total ?? 0;
  const sch = f?.schedule;
  const exp = sch?.expiresOn ? (() => { const [y, m, d] = sch.expiresOn!.split('-').map(Number); return m + '/' + d + '/' + y; })() : null;
  const build = b?.build ?? [];
  const inputS = 'width:100%;height:30px;padding:0 9px;border:1px solid rgba(34,39,46,.16);border-radius:8px;background:#FBF8F1;font-size:13px;color:#22272E;font-family:inherit;box-sizing:border-box;';
  const toggleS = (on: boolean) =>
    'display:flex;align-items:center;gap:8px;flex-wrap:nowrap;height:26px;padding:0 11px;border-radius:8px;font-size:13px;font-weight:600;text-decoration:none;' +
    (on ? 'background:rgba(47,138,111,.14);color:#12503C;border:1px solid rgba(47,138,111,.36);' : 'background:#FBF8F1;color:#54606C;border:1px solid rgba(34,39,46,.16);');
  const dotS = (on: boolean) => 'width:8px;height:8px;border-radius:50%;flex:0 0 8px;background:' + (on ? '#1F6B52' : '#54606C') + ';';

  return (
    <div data-screen-label="Finance · Billing" data-testid="finance-billing" style={sx('height:100%;flex:1 1 auto;min-height:0;display:flex;flex-direction:column;gap:10px;min-width:0;')}>
      {topLine && (
        <div data-testid="billing-top-line" style={sx('flex:0 0 auto;display:flex;align-items:center;gap:8px 14px;flex-wrap:wrap;padding:12px 14px;margin-bottom:8px;border-radius:10px;border:1px solid rgba(34,39,46,.12);background:#FCFAF4;')}>
          <span style={sx('flex:1 1 300px;min-width:0;font-size:17px;font-weight:700;color:#22272E;line-height:1.4;')}>{topLine}</span>
          {buildLabels && <span style={sx('font-size:14px;font-weight:800;letter-spacing:.07em;color:#264C7E;background:rgba(47,92,151,.08);border-radius:3px;padding:2px 5px;')}>MODELED</span>}
          <a
            href="/capture"
            onClick={(e) => {
              pre(e);
              navigate('/capture');
            }}
            style={sx('display:inline-flex;align-items:center;min-height:40px;padding:0 16px;border-radius:9px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;color:#FCFAF4;background:#2F5C97;')}
          >
            Open Capture
          </a>
        </div>
      )}
      <div style={sx('flex:0 0 auto;display:flex;align-items:center;gap:6px 12px;flex-wrap:wrap;min-width:0;')}>
        <span style={sx('font-size:17px;font-weight:800;letter-spacing:-.015em;color:#22272E;')}>Billing</span>
        <span style={sx(chip('#54606C', 'rgba(34,39,46,.18)'))}>
          <span>{period}</span>
        </span>
        <span role="group" aria-label="Billing month" style={sx('display:inline-flex;gap:2px;')}>
          {(
            [
              ['last', 'Last month'],
              ['this', 'This month (in progress)'],
            ] as const
          ).map(([k, label]) => (
            <a key={k} href="#" aria-pressed={which === k} onClick={(e) => { pre(e); setWhich(k); setSubmitted(null); }} style={sx(seg(which === k))}>
              <span>{label}</span>
            </a>
          ))}
        </span>
        <span style={sx('font-size:14px;color:#54606C;')}>Atlas documents it · the facility bills it · sealed to the Truth Ledger</span>
        <span style={sx('flex:1 1 10px;')} />
        <a
          href="#"
          onClick={(e) => {
            pre(e);
            void navigator.clipboard?.writeText(summaryText()).catch(() => undefined);
            setCopied(true);
          }}
          style={sx('display:inline-flex;align-items:center;min-height:32px;color:#2F5C97;font-size:14px;font-weight:700;text-decoration:none;white-space:nowrap;')}
        >
          <span>{copied ? 'Copied' : 'Copy summary'}</span>
        </a>
      </div>
      <div style={sx('flex:0 0 auto;display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:0;border-top:1px solid rgba(34,39,46,.14);border-bottom:1px solid rgba(34,39,46,.14);min-width:0;')} data-testid="billing-tiles">
        {tiles.map((t) => (
          <div key={t.label} style={sx('padding:8px 14px 8px 0;min-width:0;')}>
            <div style={sx('font-size:14px;color:#54606C;overflow-wrap:break-word;')}>
              <span>{t.label}</span>
            </div>
            <div data-countup="1" style={sx(figS(t.c, 26))}>
              <span>{t.fig}</span>
            </div>
            <div style={sx('font-size:14px;color:#54606C;overflow-wrap:break-word;')} title={t.note}>
              <span>{t.note}</span>
            </div>
          </div>
        ))}
      </div>
      {err && (
        <div role="alert" style={sx('font-size:14px;color:#B3261E;')}>
          {err}
        </div>
      )}
      <div style={sx('flex:1 0 auto;display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,320px),1fr));gap:14px 26px;align-items:stretch;min-width:0;')}>
        <div style={sx('min-width:0;min-height:0;display:flex;flex-direction:column;gap:6px;')}>
          <div style={sx(HEAD)}>
            <span style={sx(TITLE)}>Reimbursement capture</span>
            <span style={sx(META)}>this period</span>
          </div>
          <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(130px,1fr));gap:6px 16px;')} data-testid="billing-capture">
            {capture.map((c) => (
              <div key={c.k} style={sx('min-width:0;padding:3px 0;')}>
                <div style={sx('font-size:14px;color:#54606C;')}>
                  <span>{c.k}</span>
                </div>
                <div style={sx(figS(c.c, 19))}>
                  <span>{c.v}</span>
                </div>
                <div style={sx('font-size:14px;color:#54606C;overflow-wrap:break-word;')} title={c.note}>
                  <span>{c.note}</span>
                </div>
              </div>
            ))}
          </div>
          <div style={sx('font-size:14px;color:#54606C;line-height:1.4;text-wrap:pretty;')}>
            <span>{agingLine}</span>
          </div>
          <div style={sx('margin-top:6px;')}>
            <div style={sx(HEAD)}>
              <span style={sx(TITLE)}>Claims</span>
              <span style={sx(META)}>
                <span>{period}</span>
                {facMoney && <span>{' · ' + whole(facMoney.expectedCents) + ' expected · ' + whole(facMoney.paidCents) + ' paid' + (facMoney.deniedCents ? ' · ' + whole(facMoney.deniedCents) + ' denied' : '')}</span>}
              </span>
              <span style={sx('flex:1 1 10px;')} />
              {desk && claims.length > 0 && (
                <>
                  <a href="#" title="Download the 837P X12 file for a clearinghouse" onClick={(e) => { pre(e); void download('x12'); }} style={sx(LINK)}>
                    <span>{dl === 'x12' ? 'Preparing' : '837P'}</span>
                  </a>
                  <a href="#" title="Download the claims as CSV for the biller" onClick={(e) => { pre(e); void download('csv'); }} style={sx(LINK)}>
                    <span>{dl === 'csv' ? 'Preparing' : dl === 'failed' ? 'Download failed' : 'CSV'}</span>
                  </a>
                </>
              )}
              {readOnlyFacility ? (
                <span style={sx(META)}>Read only · Aithora bills as your agent</span>
              ) : (
              <a
                href="#"
                aria-disabled={!canAct || busy === 'gen'}
                title={roTitle}
                onClick={(e) => {
                  pre(e);
                  if (canAct && busy !== 'gen') void generate();
                }}
                style={sx(btn(!claims.length) + off)}
              >
                <span>{claims.length ? 'Regenerate' : 'Generate'}</span>
              </a>
              )}
            </div>
          </div>
          <div style={sx('font-size:14px;color:#3C454F;line-height:1.45;text-wrap:pretty;')}>
            <span>
              {claims.length
                ? claims.length + ' claims drafted for this period. Each carries its audit bundle: the time entries, the attested note, the care plan and the diagnoses.'
                : genErr
                  ? 'No practitioner enrolled yet. A claim needs an attesting NPI before it can be drafted, so add one below first.'
                  : 'No materialised claims for this period yet. Generate drafts every eligible CCM, BHI and RPM claim from documented care time, attaching the audit bundle: the time entries, the attested note, the care plan and the diagnoses.'}
            </span>
          </div>
          <div style={sx('display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:0;min-width:0;')}>
            {flow.map(([k, v, t, c]) => (
              <div key={k} style={sx('min-width:0;padding:4px 8px 4px 0;border-top:3px solid ' + c + ';')}>
                <div style={sx('overflow-wrap:break-word;')}>
                  <span style={sx('font-size:14px;font-weight:700;color:#22272E;')}>
                    <span>{k}</span>
                  </span>
                </div>
                <div style={sx('font-size:14px;font-weight:800;color:#22272E;overflow-wrap:break-word;')}>
                  <span>{v}</span>
                </div>
                <div style={sx('font-size:14px;color:#54606C;overflow-wrap:break-word;')} title={t}>
                  <span>{t}</span>
                </div>
              </div>
            ))}
          </div>
          {claims.length > 0 && (
            <div style={sx('overflow-x:auto;min-width:0;')} data-testid="billing-claims">
              <div style={sx('min-width:420px;display:grid;grid-template-columns:minmax(0,1fr) 80px 90px 100px 80px;gap:8px;padding:4px 0;border-bottom:1px solid rgba(34,39,46,.11);')}>
                {(
                  [
                    ['Resident', ''],
                    ['Code', 'right'],
                    ['Amount', 'right'],
                    ['Attested by', 'right'],
                    ['State', 'right'],
                  ] as const
                ).map(([h, a]) => (
                  <span key={h} style={sx(CAPT + (a ? 'text-align:' + a + ';' : ''))}>
                    {h}
                  </span>
                ))}
              </div>
              {claims.map((c, i) => (
                <div key={c.residentId + c.cpt + i} style={sx('min-width:420px;display:grid;grid-template-columns:minmax(0,1fr) 80px 90px 100px 80px;gap:8px;align-items:center;padding:5px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
                  <span style={sx(cellS('', 600))}>{c.resident}</span>
                  <span style={sx(cellS('right') + MONO)} title={c.cpt ? undefined : c.description}>{c.cpt || 'masked'}</span>
                  <span style={sx(cellS('right', 700))}>{'$' + Math.round(c.amount)}</span>
                  <span style={sx(cellS('right', 400, '#54606C') + 'position:relative;overflow:visible;')}>
                    {desk && canAct ? (
                      <a href="#" aria-haspopup="menu" aria-label={'Biller for ' + c.resident + ' ' + c.cpt} onClick={(e) => { pre(e); setMenu(isOpen(c, 'biller') ? null : { key: keyOf(c), kind: 'biller', ...at(e.currentTarget) }); }} style={sx('color:inherit;text-decoration:none;')}>
                        <span>{billerText(c)}</span>
                      </a>
                    ) : (
                      <span>{billerText(c)}</span>
                    )}
                    {isOpen(c, 'biller') && (
                      <RowMenu
                        at={menu!}
                        label="Biller"
                        items={prac.map((p) => ({ id: p.id, text: [p.firstName, p.lastName].filter(Boolean).join(' ') + ', ' + p.credential, on: c.biller?.id === p.id }))}
                        empty="Add a practitioner below first."
                        onPick={(id) => assignBiller(c, id)}
                        onClose={() => setMenu(null)}
                      />
                    )}
                  </span>
                  <span style={sx('text-align:right;position:relative;min-width:0;')}>
                    {desk && canAct && (CLAIM_NEXT[c.status as ClaimStatus] ?? []).length > 0 ? (
                      <a href="#" aria-haspopup="menu" aria-label={'Claim state for ' + c.resident + ' ' + c.cpt} onClick={(e) => { pre(e); setMenu(isOpen(c, 'state') ? null : { key: keyOf(c), kind: 'state', ...at(e.currentTarget) }); }} style={sx(CHIP_GREY + 'text-decoration:none;')}>
                        {stateWord(c.status)}
                      </a>
                    ) : (
                      <span style={sx(CHIP_GREY + 'text-align:right;')}>{stateWord(c.status)}</span>
                    )}
                    {isOpen(c, 'state') && (
                      <RowMenu
                        at={menu!}
                        label="Move claim to"
                        items={(CLAIM_NEXT[c.status as ClaimStatus] ?? []).map((st) => ({ id: st, text: NEXT_WORD[st], on: false }))}
                        onPick={(st) => {
                          if (st === 'denied') {
                            setMenu(null);
                            setDeny(c);
                          } else moveClaim(c, st as ClaimStatus);
                        }}
                        onClose={() => setMenu(null)}
                      />
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
          <div style={sx('margin-top:6px;')}>
            <div style={sx(HEAD)}>
              <span style={sx(TITLE)}>Billing practitioners</span>
              <span style={sx(META)}>the enrolled NPI who bills each claim</span>
            </div>
          </div>
          <div style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>
            <span>{prac.length ? prac.length + ' practitioner enrolled. Claims attest under their NPI.' : 'No practitioners yet. Add the MD, DO, NP, PA, CNS or CNM who will attest and bill.'}</span>
          </div>
          <div style={sx('display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:6px 10px;align-items:end;')}>
            {(
              [
                ['NPI', 'npi', '10 digits'],
                ['First name', 'first', ''],
                ['Last name', 'last', ''],
              ] as const
            ).map(([label, key, ph]) => (
              <div key={key} style={sx('min-width:0;')}>
                <div style={sx('font-size:14px;color:#54606C;font-weight:600;margin-bottom:3px;overflow-wrap:break-word;')}>{label}</div>
                <input
                  ref={key === 'npi' ? npiRef : undefined}
                  type="text"
                  aria-label={label}
                  value={form[key]}
                  placeholder={ph}
                  readOnly={!canAct}
                  onChange={(e) => {
                    setForm((x) => ({ ...x, [key]: e.target.value }));
                    if (key === 'npi') setGenErr(false);
                  }}
                  style={sx(inputS)}
                />
              </div>
            ))}
            <div style={sx('min-width:0;')}>
              <div style={sx('font-size:14px;color:#54606C;font-weight:600;margin-bottom:3px;overflow-wrap:break-word;')}>Credential</div>
              <Ctl
                live={canAct}
                why={roTitle}
                aria-label="Credential"
                onClick={() => setForm((x) => ({ ...x, cred: (x.cred + 1) % CREDS.length }))}
                style={'display:flex;align-items:center;height:30px;padding:0 9px;border:1px solid rgba(34,39,46,.16);border-radius:8px;background:#FBF8F1;font-size:13px;color:#22272E;text-decoration:none;box-sizing:border-box;'}
              >
                <span>{CREDS[form.cred]}</span>
                <span style={sx('flex:1;')} />
                {canAct && <span style={sx('color:#54606C;')}>{'▾'}</span>}
              </Ctl>
            </div>
            <div style={sx('min-width:0;')}>
              <div style={sx('font-size:14px;color:#54606C;font-weight:600;margin-bottom:3px;overflow-wrap:break-word;')}>PTAN</div>
              <input type="text" aria-label="PTAN" value={form.ptan} readOnly={!canAct} onChange={(e) => setForm((x) => ({ ...x, ptan: e.target.value }))} style={sx(inputS)} />
            </div>
            <div style={sx('min-width:0;')}>
              <div style={sx('font-size:14px;color:#54606C;font-weight:600;margin-bottom:3px;overflow-wrap:break-word;')}>Medicare enrolled</div>
              <Ctl live={canAct} why={roTitle} aria-pressed={form.enrolled} onClick={() => setForm((x) => ({ ...x, enrolled: !x.enrolled }))} style={toggleS(form.enrolled)}>
                <span style={sx(dotS(form.enrolled))} />
                <span>{form.enrolled ? 'Yes' : 'No'}</span>
              </Ctl>
            </div>
          </div>
          {(
            <div style={sx('display:flex;')}>
              <a
                href="#"
                aria-disabled={!canAct || undefined}
                title={roTitle}
                onClick={(e) => {
                  pre(e);
                  if (canAct && busy !== 'prac') void addPrac();
                }}
                style={sx(
                  'display:inline-flex;align-items:center;justify-content:center;height:32px;padding:0 14px;margin-top:4px;border-radius:8px;font-size:13px;font-weight:700;text-decoration:none;white-space:nowrap;' +
                    (pracErr ? 'background:rgba(179,38,30,.09);color:#B3261E;border:1px solid rgba(179,38,30,.34);' : pracValid ? 'background:#2F5C97;color:#fff;' : 'background:#FCFAF4;color:#54606C;border:1px solid rgba(47,92,151,.3);') + off,
                )}
              >
                <span>{pracErr ? 'An NPI (10 digits) and a last name are required' : 'Add practitioner'}</span>
              </a>
            </div>
          )}
          <div style={sx('margin-top:8px;display:flex;align-items:baseline;gap:2px 10px;flex-wrap:wrap;padding-bottom:6px;border-bottom:1px solid rgba(34,39,46,.12);')}>
            <span style={sx('font-size:14px;font-weight:800;color:#22272E;')}>Claim pipeline</span>
            <span style={sx('font-size:14px;color:#54606C;')}>count and dollars at each step · LIVE</span>
          </div>
          <div style={sx('display:grid;grid-template-columns:repeat(5,minmax(0,1fr));gap:0 8px;min-width:0;')}>
            {pipe.map((p) => (
              <div key={p.k} style={sx('min-width:0;padding:2px 0;')}>
                <div style={sx('font-size:14px;color:#54606C;overflow-wrap:break-word;')}>
                  <span>{p.k}</span>
                </div>
                <div style={sx('font-size:17px;font-weight:800;color:' + p.c + ';font-variant-numeric:tabular-nums;line-height:1.15;')}>
                  <span>{p.n}</span>
                </div>
                <div style={sx('font-size:14px;color:#54606C;font-variant-numeric:tabular-nums;')}>
                  <span>{p.amt}</span>
                </div>
              </div>
            ))}
          </div>
          {(desk ? allPrac : prac).map((p) => (
            <div key={p.id} style={sx('display:flex;align-items:center;gap:6px 11px;flex-wrap:wrap;padding:5px 0;border-bottom:1px solid rgba(34,39,46,.07);' + (p.active ? '' : 'opacity:.6;'))}>
              <span style={sx('font-size:13px;font-weight:700;color:#22272E;')}>
                <span>{[p.firstName, p.lastName].filter(Boolean).join(' ') + ', ' + p.credential}</span>
              </span>
              <span style={sx('font-size:14px;color:#54606C;')}>
                <span>{'NPI ' + p.npi}</span>
              </span>
              <span style={sx('flex:1;')} />
              <span style={sx(p.medicareEnrolled ? CHIP_OK : CHIP_GREY)}>
                <span>{p.medicareEnrolled ? 'MEDICARE ENROLLED' : 'NOT ENROLLED'}</span>
              </span>
              {desk && canAct && p.active && (
                authed[p.id] ? (
                  <span style={sx(CHIP_OK)}>
                    <span>AGENT AUTHORIZED</span>
                  </span>
                ) : (
                  <a href="#" onClick={(e) => { pre(e); setAuthFor({ id: p.id, name: [p.firstName, p.lastName].filter(Boolean).join(' ') }); }} style={sx(LINK)}>
                    <span>Record billing-agent authorization</span>
                  </a>
                )
              )}
              {desk && canAct && (
                <a href="#" onClick={(e) => { pre(e); if (busy !== 'prac:' + p.id) togglePrac(p.id, !p.active); }} style={sx(LINK)}>
                  <span>{p.active ? 'Deactivate' : 'Reactivate'}</span>
                </a>
              )}
            </div>
          ))}
        </div>

        <div style={sx('min-width:0;min-height:0;display:flex;flex-direction:column;gap:4px;')}>
          <div style={sx(HEAD)}>
            <span style={sx(TITLE)}>Code catalog</span>
            <span style={sx(META)}>AI capture</span>
            <span style={sx('flex:1 1 10px;')} />
            <span style={sx('font-size:14px;color:#54606C;')}>
              {/* Read live from the code table (GET /reimbursement/codes count), never a constant. */}
              <span>{codesQ.data?.count != null
                ? codesQ.data.count + ' billable codes · ' + (codesQ.data.companionCount ?? 0) + ' Atlas can document'
                : 'Code count not available'}</span>
            </span>
          </div>
          <div style={sx('flex:1 1 0;min-height:120px;overflow-y:auto;min-width:0;')} data-testid="billing-codes">
            {codes.map((c) => (
              <div key={c.code} style={sx('display:grid;grid-template-columns:52px minmax(0,1fr) auto;gap:8px;align-items:center;padding:5px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
                <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;color:#2F5C97;')}>
                  <span>{c.code}</span>
                </span>
                <span style={sx('min-width:0;overflow-wrap:break-word;')} title={c.name + ' · $' + Math.round(c.rateCents / 100) + ' / ' + perWord(c.frequency)}>
                  <span style={sx('font-size:13px;color:#22272E;')}>
                    <span>{c.name}</span>
                  </span>{' '}
                  <span style={sx('font-size:14px;color:#54606C;')}>
                    · <span>{'$' + Math.round(c.rateCents / 100) + ' / ' + perWord(c.frequency)}</span>
                  </span>
                </span>
                <span style={sx('font-size:14px;font-weight:700;letter-spacing:.04em;white-space:nowrap;text-align:right;color:' + (c.aiCapture === 'assists' ? '#1F6B52' : '#54606C') + ';')}>
                  <span>{aiChip(c)}</span>
                </span>
              </div>
            ))}
          </div>
          {desk && <MedicaidLookup client={client} />}
          <div style={sx('flex:0 0 auto;margin-top:6px;')}>
            <div style={sx(HEAD)}>
              <span style={sx(TITLE)}>Stuck, by reason</span>
              <span style={sx(META)}>
                <span>{(b && !(demoBuilding && b.real === false) ? badgeOf({ basis: 'measured' }, b.real) + ' · ' : '') + 'would be denied if sent today'}</span>
              </span>
            </div>
          </div>
          {(b?.stuck ?? []).map((r) => (
            <div key={r.reason} style={sx('flex:0 0 auto;display:flex;align-items:center;gap:4px 10px;padding:4px 0;border-bottom:1px solid rgba(34,39,46,.07);min-width:0;')}>
              <span style={sx('flex:1 1 auto;min-width:0;overflow-wrap:break-word;')} title={r.risk}>
                <span style={sx('font-size:13px;font-weight:700;color:#22272E;')}>
                  <span>{r.reason}</span>
                </span>{' '}
                <span style={sx('font-size:14px;color:#B3261E;font-weight:700;')}>
                  · <span>{r.risk}</span>
                </span>
              </span>
              <span style={sx('font-size:14px;color:#3C454F;font-variant-numeric:tabular-nums;white-space:nowrap;')}>
                <span>{r.residents + ' res.'}</span>
              </span>
              <span style={sx('font-size:13px;font-weight:800;color:#22272E;font-variant-numeric:tabular-nums;white-space:nowrap;min-width:52px;text-align:right;')}>
                <span>{whole(r.amountCents)}</span>
              </span>
              <a
                href="#"
                onClick={(e) => {
                  pre(e);
                  if (r.view === 'practitioner') npiRef.current?.focus();
                  else navigate('/worklist');
                }}
                style={sx('font-size:14px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;')}
              >
                <span>{r.action}</span> {'→'}
              </a>
            </div>
          ))}
          {!b && notServed(bill.error) && <span style={sx('font-size:14px;color:#54606C;padding:4px 0;')}>{plain('Not served yet: the pre-claim gates by reason.')}</span>}
          <div style={sx('flex:0 0 auto;display:flex;flex-direction:column;gap:4px;margin-top:6px;')}>
            <div style={sx('display:flex;align-items:baseline;gap:4px 10px;flex-wrap:wrap;')}>
              <span style={sx('font-size:14px;font-weight:800;color:#22272E;')}>
                <span>{verified + ' of ' + total + ' rates verified'}</span>
              </span>
              <span style={sx('font-size:14px;color:#54606C;')}>
                {/* Customers read plain words; the label word is for Developer and Super Admin. */}
                <span>{total - verified + (buildLabels ? ' ESTIMATED, labelled on every figure' : ' estimated, marked est. on every figure')}</span>
              </span>
            </div>
            <div style={sx('display:flex;height:8px;border-radius:4px;overflow:hidden;background:rgba(34,39,46,.08);')}>
              <span style={sx('width:' + (total ? ((verified / total) * 100).toFixed(1) : '0') + '%;background:#1F6B52;')} />
            </div>
            <span style={sx('font-size:14px;color:#54606C;')}>
              <span>{sch ? 'Fee schedule CY' + sch.year + ' · conversion factor $' + sch.conversionFactor.toFixed(4) + ' (' + sch.cfBasis + ')' + (exp ? ' · expires ' + exp : '') : 'Fee schedule not loaded'}</span>
            </span>
          </div>
          <div style={sx('flex:0 0 auto;margin-top:6px;')}>
            <div style={sx(HEAD)}>
              <span style={sx(TITLE)}>Claim health</span>
              <span style={sx(META)}>against the industry target</span>
            </div>
          </div>
          {health.map(([k, why, t]) => (
            <div key={k} style={sx('flex:0 0 auto;display:grid;grid-template-columns:minmax(0,1fr) 56px 74px;gap:8px;align-items:baseline;padding:4px 0;border-bottom:1px solid rgba(34,39,46,.07);')}>
              <span style={sx('min-width:0;overflow-wrap:break-word;')} title={why}>
                <span style={sx('font-size:13px;color:#22272E;font-weight:700;')}>
                  <span>{k}</span>
                </span>{' '}
                <span style={sx('font-size:14px;color:#54606C;')}>
                  · <span>{why}</span>
                </span>
              </span>
              <span style={sx('font-size:13px;font-weight:800;text-align:right;font-variant-numeric:tabular-nums;color:#54606C;')}>–</span>
              <span style={sx('font-size:14px;text-align:right;color:#54606C;font-variant-numeric:tabular-nums;white-space:nowrap;')}>
                <span>{'target ' + t}</span>
              </span>
            </div>
          ))}
          <span style={sx('flex:0 0 auto;font-size:14px;color:#54606C;')}>
            <span>{claims.length ? 'Scored once claims are adjudicated. Nothing here is invented.' : 'No claims yet, so there is nothing to score. Nothing here is invented.'}</span>
          </span>
        </div>

        <div style={sx('min-width:0;min-height:0;display:flex;flex-direction:column;gap:6px;')}>
          <div style={sx(HEAD)}>
            <span style={sx(TITLE)}>Clearinghouse</span>
            <span style={sx(META)}>electronic submission</span>
          </div>
          <div style={sx('display:flex;align-items:center;gap:6px 12px;flex-wrap:wrap;')}>
            <span style={sx('font-size:14px;color:#54606C;font-weight:600;')}>Provider</span>
            {(
              [
                ['stedi', 'Stedi'],
                ['claimmd', 'Claim.MD'],
              ] as const
            ).map(([id, name]) => (
              <Ctl
                key={id}
                live={chLive}
                why={chWhy}
                aria-pressed={ch?.provider === id}
                onClick={() => {
                  if (!ch) return;
                  setCh({ ...ch, provider: id });
                  setSaved(false);
                }}
                style={seg((ch?.provider ?? 'stedi') === id)}
              >
                <span>{name}</span>
              </Ctl>
            ))}
            <span style={sx('flex:1 1 100%;height:0;')} />
            <span style={sx('font-size:14px;color:#54606C;font-weight:600;')}>Environment</span>
            {(
              [
                ['sandbox', 'Sandbox'],
                ['production', 'Production'],
              ] as const
            ).map(([id, name]) => (
              <Ctl
                key={id}
                live={chLive}
                why={chWhy}
                aria-pressed={ch?.environment === id}
                onClick={() => {
                  if (!ch) return;
                  setCh({ ...ch, environment: id });
                  setSaved(false);
                }}
                style={seg((ch?.environment ?? 'sandbox') === id)}
              >
                <span>{name}</span>
              </Ctl>
            ))}
          </div>
          <div style={sx('display:flex;align-items:center;gap:6px 11px;flex-wrap:wrap;')}>
            <Ctl
              live={chLive}
              why={chWhy}
              aria-pressed={!!ch?.enabled}
              onClick={() => {
                if (!ch) return;
                setCh({ ...ch, enabled: !ch.enabled });
                setSaved(false);
              }}
              style={
                'display:inline-flex;align-items:center;gap:8px;height:30px;padding:0 12px;border-radius:8px;font-size:13px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
                (ch?.enabled ? 'background:rgba(47,138,111,.14);color:#12503C;border:1px solid rgba(47,138,111,.36);' : 'background:#FBF8F1;color:#54606C;border:1px solid rgba(34,39,46,.16);')
              }
            >
              <span style={sx(dotS(!!ch?.enabled))} />
              <span>{ch?.enabled ? 'Enabled' : 'Off'}</span>
            </Ctl>
            <span style={sx(keyOk ? CHIP_OK : CHIP_GREY)}>
              <span>{keyOk ? 'KEY SAVED' : 'NO KEY YET'}</span>
            </span>
            <span style={sx('flex:1 1 10px;')} />
            {(
              <a
                href="#"
                aria-disabled={!canAct || undefined}
                title={roTitle}
                onClick={(e) => {
                  pre(e);
                  if (canAct && busy !== 'save') void saveSetup();
                }}
                style={sx(btn(!saved) + off)}
              >
                <span>{saved ? 'Saved' : 'Save setup'}</span>
              </a>
            )}
            {(
              <a
                href="#"
                aria-disabled={!canAct || !canSubmit}
                title={roTitle}
                onClick={(e) => {
                  pre(e);
                  if (canAct && busy !== 'submit') void submit();
                }}
                style={sx(
                  'display:inline-flex;align-items:center;justify-content:center;height:32px;padding:0 14px;border-radius:8px;font-size:13px;font-weight:600;text-decoration:none;flex:0 0 auto;white-space:nowrap;' +
                    (canSubmit ? 'background:#2F5C97;color:#fff;' : 'background:#FCFAF4;color:#54606C;border:1px solid rgba(34,39,46,.2);') + off,
                )}
              >
                <span>{submitted !== null ? 'Submitted' : 'Submit ready claims'}</span>
              </a>
            )}
          </div>
          <div style={sx('font-size:14px;color:#54606C;line-height:1.4;')}>
            <span>
              {submitted !== null
                ? submitted + ' claims submitted to ' + provName + ' in ' + envName + '.' + (ch?.environment === 'sandbox' ? ' Nothing reaches a payer from a sandbox environment.' : '')
                : monthOpen
                  ? 'This month is still in progress. Its claims are submitted once the month closes; until then Capture shows who has qualified.'
                  : !claims.length
                  ? 'Nothing to submit yet. Generate the period’s claims first.'
                  : !ch?.enabled
                    ? 'Submission is off. Turn it on, save the setup, and the ready claims can go.'
                    : !saved
                      ? 'Save the setup before submitting, so the key and payer IDs are stored.'
                      : claims.length + ' claims are ready to submit.'}
            </span>
          </div>
          {ident && (
            <div data-testid="billing-identity" style={sx('display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:6px 10px;')}>
              {(
                [
                  ['Billing NPI', 'billingNpi'],
                  ['Tax ID', 'taxId'],
                  ['Payer ID', 'payerId'],
                  ['Payer name', 'payerName'],
                ] as const
              ).map(([label, key]) => (
                <label key={key} style={sx('min-width:0;font-size:14px;color:#54606C;font-weight:600;')}>
                  {label}
                  <input
                    type="text"
                    aria-label={label}
                    value={ident[key]}
                    readOnly={!canAct}
                    onChange={(e) => {
                      setIdent({ ...ident, [key]: e.target.value });
                      setSaved(false);
                    }}
                    style={sx(inputS + 'margin-top:3px;')}
                  />
                </label>
              ))}
            </div>
          )}
          <div data-testid="invoice-preview" style={sx('display:flex;flex-direction:column;gap:4px;margin-top:6px;')}>
            <div style={sx(HEAD)}>
              <span style={sx(TITLE)}>Monthly invoice</span>
              <span style={sx(META)}>a dry run · nothing is sent to Stripe</span>
              <span style={sx('flex:1 1 10px;')} />
              <a href="#" onClick={(e) => { pre(e); if (busy !== 'inv') void preview(); }} style={sx(LINK)}>
                <span>{"Preview this month's invoice"}</span>
              </a>
            </div>
            {inv && (
              <span style={sx('font-size:14px;color:#3C454F;line-height:1.45;')}>
                {inv.billable
                  ? inv.period + ': ' + whole(inv.amountCents) + (inv.occupiedBeds != null && inv.rateCents != null ? ' · ' + inv.occupiedBeds + ' occupied beds × ' + whole(inv.rateCents) : '') + (inv.description ? ' · ' + inv.description : '')
                  : inv.period + ': nothing to invoice. ' + (inv.reason ?? '')}
              </span>
            )}
          </div>
          <YourBill
            pilot={pilot}
            beds={beds}
            rateUsd={b ? b.tiles.ratePerResidentCents / 100 : null}
            subscriptionUsd={b ? b.subscriptionCents / 100 : null}
            totalUsd={b ? b.totalCents / 100 : null}
            build={build}
            share={share}
          />
          {/* Super Admin only: facility users never see these controls (design blShareCtl). */}
          {superAdmin && <ShareSettings facName={facName ?? 'This building'} value={share} onChange={setShare} />}
        </div>
      </div>
      {authFor && (
        <ConfirmDialog
          title={`Record ${authFor.name}'s billing-agent authorization?`}
          body="Their signed authorization for Aithora to send their signed claims as billing agent. Without it no claim of theirs is sent."
          confirmLabel="Record"
          reason={{ label: 'Where the signed authorization is kept', placeholder: 'e.g. DocuSign envelope 4F2A, or the file name', required: true }}
          onConfirm={async (ref) => {
            await captureAuthorizeBillingAgent(client, { params: { id: authFor.id }, body: { documentRef: ref } });
            setAuthed((x) => ({ ...x, [authFor.id]: true }));
          }}
          onClose={() => setAuthFor(null)}
        />
      )}
      {deny && (
        <ConfirmDialog
          title={'Deny ' + deny.cpt + ' for ' + deny.resident}
          body="Record the payer's denial on this claim. It can be corrected and resubmitted."
          confirmLabel="Record denial"
          danger
          reason={{ label: 'Denial reason', placeholder: 'The payer’s reason code or words (required)' }}
          onConfirm={async (why) => {
            await setStatus(deny, 'denied', why);
            claimsQ.reload();
          }}
          onClose={() => setDeny(null)}
        />
      )}
    </div>
  );
}

const LINK = 'display:inline-flex;align-items:center;font-size:14px;font-weight:700;color:#2F5C97;text-decoration:none;white-space:nowrap;';
/** Where a menu opens: under its anchor, right-aligned (fixed, so the table's scroller never clips it). */
const at = (el: Element) => {
  const r = el.getBoundingClientRect();
  return { top: Math.round(r.bottom + 4), right: Math.round(window.innerWidth - r.right) };
};
const stateWord = (s: string) => (s === 'awaiting_signature' ? 'Signing' : s.charAt(0).toUpperCase() + s.slice(1));
/** What each move is called in a claim's menu. */
const NEXT_WORD: Record<ClaimStatus, string> = {
  draft: 'Back to draft',
  ready: 'Back to ready',
  awaiting_signature: 'Send for signature',
  submitted: 'Mark submitted',
  accepted: 'Mark accepted',
  denied: 'Record denial',
  paid: 'Mark paid',
};

/** A small menu under a claim's cell, in the design's panel style. */
function RowMenu({ at: pos, label, items, empty, onPick, onClose }: { at: { top: number; right: number }; label: string; items: { id: string; text: string; on: boolean }[]; empty?: string; onPick: (id: string) => void; onClose: () => void }) {
  return (
    <>
      <span role="presentation" onClick={onClose} style={sx('position:fixed;inset:0;z-index:40;')} />
      <span role="menu" aria-label={label} style={sx('position:fixed;top:' + pos.top + 'px;right:' + pos.right + 'px;z-index:41;display:flex;flex-direction:column;min-width:170px;padding:4px;border-radius:8px;background:#FCFAF4;border:1px solid rgba(34,39,46,.16);box-shadow:0 8px 24px rgba(34,39,46,.14);text-align:left;')}>
        {items.length === 0 && <span style={sx('padding:6px 9px;font-size:14px;color:#54606C;white-space:normal;')}>{empty}</span>}
        {items.map((it) => (
          <a
            key={it.id}
            href="#"
            role="menuitem"
            aria-current={it.on || undefined}
            onClick={(e) => {
              pre(e);
              onPick(it.id);
            }}
            style={sx('display:block;padding:6px 9px;border-radius:6px;font-size:14px;font-weight:600;text-decoration:none;white-space:nowrap;color:' + (it.on ? '#2F5C97' : '#22272E') + ';')}
          >
            {it.text}
          </a>
        ))}
      </span>
    </>
  );
}
